import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import {
    AlertTriangle, TrendingUp, TrendingDown, Warehouse, Wallet, ShoppingCart,
    PackageCheck, Truck, Users, ArrowUpRight, ArrowDownRight, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const ratioColor = (r, green, amber) => {
    if (r == null) return 'bg-slate-200 text-slate-500';
    if (r <= green) return 'bg-emerald-100 text-emerald-700';
    if (r <= amber) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
};

const Gauge = ({ label, value, green, amber, testId }) => {
    const pct = value == null ? 0 : Math.min(value, 100);
    const color = value == null ? '#cbd5e1' : value <= green ? '#059669' : value <= amber ? '#d97706' : '#dc2626';
    const r = 44, c = 2 * Math.PI * r;
    return (
        <div className="flex flex-col items-center" data-testid={testId}>
            <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" />
                <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
                    strokeDasharray={`${(pct / 100) * c} ${c}`} transform="rotate(-90 60 60)" />
                <text x="60" y="58" textAnchor="middle" fontSize="22" fontWeight="700" fill="#0f172a">
                    {value == null ? '—' : `${value}%`}
                </text>
                <text x="60" y="76" textAnchor="middle" fontSize="9" fill="#94a3b8">cible ≤ {green}%</text>
            </svg>
            <span className="text-xs font-semibold text-slate-600 -mt-1">{label}</span>
        </div>
    );
};

export default function DashboardV2() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canViewLogins = ['admin', 'supervisor'].includes(user?.role) || (user?.permissions || []).includes('users_logins_view');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/dashboard/v2');
                setData(res.data);
            } catch { /* silencieux */ }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
    if (!data) return <div className="py-20 text-center text-slate-400">Erreur de chargement du tableau de bord</div>;

    const a = data.alerts || {};
    const alertSegments = [];
    if (a.siege_transit?.count > 0) alertSegments.push({
        icon: Warehouse, label: `${a.siege_transit.count} réception(s) Siège à ventiler (${formatCurrency(a.siege_transit.value)})`,
        sub: a.siege_transit.oldest_age_days > 1 ? `la + ancienne : ${a.siege_transit.oldest_age_days} j` : null,
        to: '/orders?tab=transfers', testId: 'alert-siege-transit',
    });
    if (a.unpaid?.count > 0) alertSegments.push({
        icon: Wallet, label: `${a.unpaid.count} facture(s) à régler (${formatCurrency(a.unpaid.total)})`,
        sub: a.unpaid.over_30d_count > 0 ? `${a.unpaid.over_30d_count} de + de 30 j` : null,
        to: '/finance', testId: 'alert-unpaid',
    });
    if (a.pending_orders?.count > 0) alertSegments.push({
        icon: ShoppingCart, label: `${a.pending_orders.count} commande(s) non envoyée(s)`,
        to: '/orders', testId: 'alert-pending-orders',
    });
    if (a.late_receptions?.count > 0) alertSegments.push({
        icon: Truck, label: `${a.late_receptions.count} commande(s) confirmée(s) sans réception (+3 j)`,
        to: '/orders', testId: 'alert-late-receptions',
    });

    const monthLabel = new Date(`${data.month}-01T00:00:00`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

    return (
        <div className="space-y-4 animate-in fade-in duration-300" data-testid="dashboard-v2">
            {/* ZONE 1 — ligne de synthèse */}
            {alertSegments.length > 0 ? (
                <div className="flex flex-wrap items-center gap-x-1 gap-y-1 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5" data-testid="dashboard-alert-bar">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mr-1" />
                    <span className="text-sm font-bold text-amber-800 mr-1">{alertSegments.length} action(s) en attente :</span>
                    {alertSegments.map((seg, i) => (
                        <React.Fragment key={seg.testId}>
                            {i > 0 && <span className="text-amber-300 mx-1">·</span>}
                            <button type="button" onClick={() => navigate(seg.to)}
                                className="text-sm text-amber-900 hover:text-amber-950 hover:underline inline-flex items-center gap-1"
                                data-testid={seg.testId}>
                                <seg.icon className="w-3.5 h-3.5 opacity-70" />
                                {seg.label}
                                {seg.sub && <span className="text-xs text-red-600 font-semibold">({seg.sub})</span>}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            ) : (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 text-sm text-emerald-700" data-testid="dashboard-all-clear">
                    <CheckCircle2 className="w-4 h-4" /> Tout est à jour — aucune action en attente.
                </div>
            )}

            {/* ZONE 2 — tuiles par site */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                {(data.sites || []).map(t => {
                    const staleSale = t.last_sale_date && (new Date() - new Date(t.last_sale_date + 'T00:00:00')) > 2 * 86400000;
                    return (
                        <Card key={t.site_id} className="relative overflow-hidden" data-testid={`site-tile-${t.site_id}`}>
                            <CardContent className="p-3">
                                <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t.site_name.replace('FE ', '')}</div>
                                <div className="mt-1 flex items-baseline gap-2">
                                    <span className="text-xl font-bold text-slate-900 tabular-nums">{formatCurrency(t.ca_week)}</span>
                                    {t.var_pct != null && (
                                        <span className={`inline-flex items-center text-xs font-semibold ${t.var_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {t.var_pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                                            {Math.abs(t.var_pct)}%
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10px] text-slate-400">CA semaine en cours</div>
                                <div className="mt-2 flex items-center justify-between">
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${ratioColor(t.ratio_month, 30, 35)}`} title="Ratio matière du mois (achats / CA net)">
                                        {t.ratio_month == null ? '—' : `${t.ratio_month}%`} <span className="font-normal opacity-70">matière</span>
                                    </span>
                                    <span className={`text-[10px] ${staleSale ? 'text-red-500 font-semibold' : 'text-slate-400'}`} title="Dernière vente saisie">
                                        {t.last_sale_date ? new Date(t.last_sale_date + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : 'aucune vente'}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* ZONE 2 — graphique + jauges */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Card className="lg:col-span-2">
                    <CardHeader className="pb-1">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-orange-500" /> CA vs Achats — 8 dernières semaines
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0" data-testid="ca-achats-chart">
                        <ResponsiveContainer width="100%" height={230}>
                            <ComposedChart data={data.weekly_series || []} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                                <Tooltip formatter={(v, n) => [formatCurrency(v), n === 'ca' ? 'CA' : 'Achats']} />
                                <Legend formatter={(v) => v === 'ca' ? 'CA encaissé' : 'Achats reçus'} wrapperStyle={{ fontSize: 12 }} />
                                <Area type="monotone" dataKey="ca" stroke="#059669" fill="#05966915" strokeWidth={2} />
                                <Line type="monotone" dataKey="achats" stroke="#f97316" strokeWidth={2} dot={{ r: 2 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-1">
                        <CardTitle className="text-sm">Ratios de {monthLabel}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex items-center justify-around pt-2">
                        <Gauge label="Ratio matière" value={data.gauges?.ratio_matiere} green={30} amber={35} testId="gauge-matiere" />
                        <Gauge label="Masse salariale" value={data.gauges?.masse_salariale} green={35} amber={45} testId="gauge-masse" />
                    </CardContent>
                </Card>
            </div>

            {/* ZONE 3 — activité */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card data-testid="top-suppliers-card">
                    <CardHeader className="pb-1">
                        <CardTitle className="text-sm flex items-center gap-2"><TrendingDown className="w-4 h-4 text-slate-400" /> Top 5 fournisseurs — {monthLabel}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-1 space-y-1.5">
                        {(data.top_suppliers || []).length === 0 && <p className="text-xs text-slate-400">Aucune réception ce mois-ci.</p>}
                        {(data.top_suppliers || []).map((s, i) => (
                            <div key={s.supplier_id} className="flex items-center gap-2 text-sm">
                                <span className="w-4 text-xs text-slate-400 font-bold">{i + 1}</span>
                                <span className="flex-1 truncate text-slate-700">{s.name}</span>
                                {s.var_pct != null && (
                                    <span className={`text-[10px] font-semibold ${s.var_pct > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                                        {s.var_pct > 0 ? '▲' : '▼'} {Math.abs(s.var_pct)}%
                                    </span>
                                )}
                                <span className="tabular-nums font-semibold text-slate-900">{formatCurrency(s.total)}</span>
                            </div>
                        ))}
                    </CardContent>
                </Card>
                <Card data-testid="last-receptions-card">
                    <CardHeader className="pb-1">
                        <CardTitle className="text-sm flex items-center gap-2"><PackageCheck className="w-4 h-4 text-slate-400" /> Dernières réceptions</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-1 space-y-1.5">
                        {(data.last_receptions || []).map((r, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                                <span className="text-[10px] font-mono text-slate-400 w-10">{r.date ? new Date(r.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '—'}</span>
                                <span className="flex-1 truncate text-slate-700">{r.supplier}</span>
                                <span className="text-[10px] text-slate-400">{(r.site || '').replace('FE ', '')}</span>
                                <span className="tabular-nums font-semibold text-slate-900">{formatCurrency(r.amount)}</span>
                            </div>
                        ))}
                    </CardContent>
                </Card>
                {canViewLogins && (
                <Card data-testid="logins-today-card">
                    <CardHeader className="pb-1">
                        <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4 text-slate-400" /> Connexions du jour</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-1 space-y-1.5">
                        {(data.logins_today || []).length === 0 && <p className="text-xs text-slate-400">Aucune connexion aujourd'hui.</p>}
                        {(data.logins_today || []).map((l) => (
                            <div key={l.username} className="flex items-center gap-2 text-sm">
                                <span className="font-mono text-[10px] text-slate-400 w-9">{l.time}</span>
                                <span className="flex-1 truncate text-slate-700">{l.name}</span>
                                <span className="text-[10px] text-slate-400">{l.device}</span>
                            </div>
                        ))}
                    </CardContent>
                </Card>
                )}
            </div>
        </div>
    );
}
